/**
 * Supabase Authentication Context
 * Replaces Firebase authentication with Supabase
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase, authHelpers, profileHelpers, Profile } from '../lib/supabase'

interface AuthContextType {
  // Auth state
  user: User | null
  session: Session | null
  profile: Profile | null
  loading: boolean
  error: AuthError | null

  // Computed properties for compatibility
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean

  // Auth methods
  signUp: (email: string, password: string, metadata?: { full_name?: string }) => Promise<any>
  signIn: (email: string, password: string) => Promise<any>
  signInWithGoogle: () => Promise<any>
  signInWithGithub: () => Promise<any>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<any>
  updatePassword: (password: string) => Promise<any>
  updateProfile: (updates: Partial<Profile>) => Promise<void>

  // Compatibility aliases for Firebase migration
  login: (email: string, password: string) => Promise<any>
  register: (email: string, password: string, displayName: string) => Promise<any>
  loginWithGoogle: () => Promise<any>
  loginWithGithub: () => Promise<any>
  logout: () => Promise<void>
  refreshUser: () => Promise<Profile | null>
  refreshUserData: () => Promise<Profile | null>

  // Helper methods
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  isInGroup: (group: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  canAccessRoute: (route: string) => boolean
  canAccessPlatform: (platform: string) => boolean
  hasGroup: (group: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const SupabaseAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<AuthError | null>(null)

  // Initialize auth state
  useEffect(() => {
    let mounted = true

    async function getInitialSession() {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (mounted) {
          if (error) {
            setError(error)
          } else {
            setSession(session)
            setUser(session?.user ?? null)

            // Load user profile if user exists
            if (session?.user) {
              await loadUserProfile(session.user.id)
            }
          }
          setLoading(false)
        }
      } catch (err) {
        if (mounted) {
          console.error('Error getting initial session:', err)
          setError(err as AuthError)
          setLoading(false)
        }
      }
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (mounted) {
          console.log('Auth state changed:', event, session?.user?.email)

          setSession(session)
          setUser(session?.user ?? null)
          setError(null)

          if (event === 'SIGNED_IN' && session?.user) {
            await loadUserProfile(session.user.id)
          } else if (event === 'SIGNED_OUT') {
            setProfile(null)
          }

          if (event === 'INITIAL_SESSION') {
            setLoading(false)
          }
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Load user profile from Supabase or create if doesn't exist
  const loadUserProfile = async (userId: string) => {
    try {
      let userProfile = await profileHelpers.getProfile(userId)

      // If profile doesn't exist, create it
      if (!userProfile && user) {
        console.log('Creating new user profile...')
        userProfile = await profileHelpers.createProfile({
          id: userId,
          email: user.email!,
          display_name: user.user_metadata?.full_name || user.email!.split('@')[0],
          email_verified: user.email_confirmed_at !== null,
          role: 'basic_user',
          is_active: true,
          account_status: 'active',
          subscription_tier: 'free'
        })
      }

      setProfile(userProfile)
    } catch (err) {
      console.error('Error loading user profile:', err)
      setError(err as AuthError)
    }
  }

  // Auth methods
  const signUp = async (email: string, password: string, metadata?: { full_name?: string }) => {
    setLoading(true)
    setError(null)

    try {
      const result = await authHelpers.signUp(email, password, metadata)
      return result
    } catch (err) {
      setError(err as AuthError)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    setError(null)

    try {
      const result = await authHelpers.signIn(email, password)
      return result
    } catch (err) {
      setError(err as AuthError)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await authHelpers.signInWithProvider('google')
      return result
    } catch (err) {
      setError(err as AuthError)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signInWithGithub = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await authHelpers.signInWithProvider('github')
      return result
    } catch (err) {
      setError(err as AuthError)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)

    try {
      await authHelpers.signOut()
      setProfile(null)
    } catch (err) {
      setError(err as AuthError)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email: string) => {
    setError(null)

    try {
      const result = await authHelpers.resetPassword(email)
      return result
    } catch (err) {
      setError(err as AuthError)
      throw err
    }
  }

  const updatePassword = async (password: string) => {
    setError(null)

    try {
      const result = await authHelpers.updatePassword(password)
      return result
    } catch (err) {
      setError(err as AuthError)
      throw err
    }
  }

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user || !profile) return

    setError(null)

    try {
      const updatedProfile = await profileHelpers.updateProfile(user.id, updates)
      setProfile(updatedProfile)
    } catch (err) {
      setError(err as AuthError)
      throw err
    }
  }

  // Refresh user profile
  const refreshUserProfile = async (): Promise<Profile | null> => {
    if (!user) return null
    await loadUserProfile(user.id)
    return profile
  }

  // Helper methods for RBAC
  const hasPermission = (permission: string): boolean => {
    if (!profile) return false
    return profile.permissions?.includes(permission) || false
  }

  const hasRole = (role: string): boolean => {
    if (!profile) return false
    return profile.role === role
  }

  const hasAnyRole = (roles: string[]): boolean => {
    if (!profile) return false
    return roles.includes(profile.role)
  }

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!profile) return false
    return permissions.some(permission => profile.permissions?.includes(permission))
  }

  const isInGroup = (group: string): boolean => {
    if (!profile) return false
    return profile.groups?.includes(group) || false
  }

  const canAccessRoute = (route: string): boolean => {
    if (!profile) return false
    // Admin and super_admin can access all routes
    if (profile.role === 'admin' || profile.role === 'super_admin') return true
    return true // Default allow for now
  }

  const canAccessPlatform = (platform: string): boolean => {
    if (!profile) return false
    return profile.platform_access?.includes(platform) || false
  }

  const value: AuthContextType = {
    // State
    user,
    session,
    profile,
    loading,
    error,

    // Computed properties for compatibility
    isAuthenticated: !!user,
    isLoading: loading,
    isInitialized: !loading,

    // Auth methods
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGithub,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,

    // Compatibility aliases for Firebase migration
    login: signIn,
    register: async (email: string, password: string, displayName: string) => {
      return signUp(email, password, { full_name: displayName })
    },
    loginWithGoogle: signInWithGoogle,
    loginWithGithub: signInWithGithub,
    logout: signOut,
    refreshUser: refreshUserProfile,
    refreshUserData: refreshUserProfile,

    // RBAC helpers
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAnyPermission,
    isInGroup,
    hasGroup: isInGroup, // Alias for isInGroup
    canAccessRoute,
    canAccessPlatform
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook to use auth context
export const useSupabaseAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}

// Hook for backward compatibility with existing code
export const useAuth = useSupabaseAuth

// Convenience hooks for user data (Firebase compatibility)
export function useCurrentUser() {
  const { profile } = useSupabaseAuth()
  return profile
}

export function useUserRole() {
  const { profile, hasRole } = useSupabaseAuth()

  // Helper function to check hierarchical access
  const hasRoleAccess = (userRole: string | null, targetRole: string): boolean => {
    if (!userRole) return false

    const roleHierarchy: Record<string, number> = {
      basic_user: 1,
      seller: 2,
      creator_admin: 3,
      admin: 4,
      super_admin: 5
    }

    const userLevel = roleHierarchy[userRole]
    const targetLevel = roleHierarchy[targetRole]

    return userLevel >= targetLevel
  }

  return {
    role: profile?.role || null,
    isBasicUser: () => hasRole('basic_user'),
    isSeller: () => hasRole('seller'),
    isCreatorAdmin: () => hasRole('creator_admin'),
    isAdmin: () => hasRole('admin'),
    isSuperAdmin: () => hasRole('super_admin'),

    // Hierarchical role access checks
    isAdminOrAbove: () => profile ? hasRoleAccess(profile.role, 'admin') : false,
    isSellerOrAbove: () => profile ? hasRoleAccess(profile.role, 'seller') : false,
    isCreatorOrAbove: () => profile ? hasRoleAccess(profile.role, 'creator_admin') : false
  }
}

export function useUserPermissions() {
  const { profile, hasPermission, hasAnyPermission } = useSupabaseAuth()

  return {
    permissions: profile?.permissions || [],
    hasPermission,
    hasAnyPermission,
    canManageUsers: () => hasPermission('users.manage'),
    canManageAgents: () => hasPermission('admin.agents.manage'),
    canCreateAgents: () => hasPermission('agents.create'),
    canManageWorkflows: () => hasPermission('workflows.manage')
  }
}

export default SupabaseAuthProvider